import type { ContactTableColumnId } from "./contacts-column-view.js";
import {
  DEFAULT_COLUMN_ORDER,
  DEFAULT_HIDDEN_COLUMNS,
  DEFAULT_PINNED_COLUMNS,
  isContactTableColumnId,
  normalizeContactColumnOrder,
  normalizePinnedColumns,
} from "./contacts-column-view.js";

/** Approved localStorage key — extend in place; do not add a second key. */
export const CONTACTS_TABLE_COLUMNS_STORAGE_KEY = "contacts-table-columns:v1";

export type ContactsTableColumnPrefs = {
  order: ContactTableColumnId[];
  hidden: ContactTableColumnId[];
  /** Pin sequence (START region). Omitted/empty in older saved prefs. */
  pinned: ContactTableColumnId[];
};

/**
 * Parse `contacts-table-columns:v1` with backwards-compatible defaults.
 * Older payloads only had `order` + `hidden`; missing `pinned` becomes [].
 */
export function parseContactsTableColumnPrefs(raw: string | null): ContactsTableColumnPrefs | null {
  if (raw == null || raw.length === 0) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as {
      order?: unknown;
      hidden?: unknown;
      pinned?: unknown;
    };
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) {
      return null;
    }
    const storedOrder = parsed.order.filter((id): id is string => typeof id === "string");
    const order = normalizeContactColumnOrder(storedOrder);
    const hidden = parsed.hidden
      .filter((id): id is string => typeof id === "string")
      .filter(isContactTableColumnId);
    // Columns added after these prefs were saved keep their default visibility.
    for (const id of DEFAULT_HIDDEN_COLUMNS) {
      if (!storedOrder.includes(id) && !hidden.includes(id)) {
        hidden.push(id);
      }
    }
    const pinnedRaw = Array.isArray(parsed.pinned)
      ? parsed.pinned.filter((id): id is string => typeof id === "string")
      : DEFAULT_PINNED_COLUMNS;
    return {
      order,
      hidden,
      pinned: normalizePinnedColumns(pinnedRaw, order),
    };
  } catch {
    return null;
  }
}

export function defaultContactsTableColumnPrefs(): ContactsTableColumnPrefs {
  return {
    order: [...DEFAULT_COLUMN_ORDER],
    hidden: [...DEFAULT_HIDDEN_COLUMNS],
    pinned: [...DEFAULT_PINNED_COLUMNS],
  };
}

export function serializeContactsTableColumnPrefs(prefs: ContactsTableColumnPrefs): string {
  return JSON.stringify({
    order: prefs.order,
    hidden: prefs.hidden,
    pinned: prefs.pinned,
  });
}
